/** Shared container-load-planner geometry helpers (Phase 11). Pure, unit-testable. */

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Footprint (along, across) of a pallet at the given orientation (mm). */
export function footprint(
  lengthMm: number,
  widthMm: number,
  orientation: number,
): { along: number; across: number } {
  return orientation === 90
    ? { along: widthMm, across: lengthMm }
    : { along: lengthMm, across: widthMm };
}

/** Axis-aligned overlap test at 1 mm resolution (half-open intervals). */
export function rectsOverlap(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width &&
    b.x < a.x + a.width &&
    a.y < b.y + b.height &&
    b.y < a.y + a.height
  );
}

/** Snap a value to the nearest grid step (default 50 mm). */
export function snap(value: number, step = 50): number {
  return Math.round(value / step) * step;
}

/** Clamp value into [min, max]. */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Convert a plan item to an axis-aligned rect in the top-view coordinate space
 * (x = distance from bulkhead, y = offset from the vehicle's left wall).
 */
export function itemToTopRect(
  posX: number,
  posY: number,
  lengthMm: number,
  widthMm: number,
  orientation: number,
): Rect {
  const fp = footprint(lengthMm, widthMm, orientation);
  return { x: posX, y: posY, width: fp.along, height: fp.across };
}

/**
 * Front/side-view rect: x = distance from bulkhead, y = height above the deck
 * floor. Pallets stand on their across-face; height is the pallet height.
 */
export function itemToSideRect(
  posX: number,
  lengthMm: number,
  heightMm: number,
  orientation: number,
): Rect {
  const fp = footprint(lengthMm, 0, orientation);
  return { x: posX, y: 0, width: fp.along, height: heightMm };
}

/** mm -> px scale that fits `mmSize` into `pxBudget` while preserving aspect. */
export function fitScale(
  mmWidth: number,
  mmHeight: number,
  pxBudget: number,
  pxHeightBudget: number,
): number {
  if (mmWidth <= 0 || mmHeight <= 0) return 1;
  return Math.min(pxBudget / mmWidth, pxHeightBudget / mmHeight);
}
